import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Plus, Save, Undo2, Loader2, TriangleAlert } from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { secondaryService } from '../../../services/secondaryService';
import MantleDataGrid, { gcell } from '../../../components/MantleDataGrid';
import { useColumnFilters, FilterPopover } from '../../../components/gridFilters';
import PickerPopover from '../../../components/PickerPopover';

// =============================================================================
// Distribution › Budget Setup › Secondary Posting
//
// Where secondary distribution lands in the ledger. Sibling of Inter-Depot
// Posting, and here for the same reason: deciding that secondary freight is
// GL 6400 is a chart-of-accounts decision with ONE owner, and leaving it on the
// capture screen is how six depots post the same movement to four accounts.
//
// SCOPED site → zone → carrier type, each level optional, MOST SPECIFIC WINS:
//
//     site,  zone,  carrier   most specific — this zone, this kind of carrier
//     site,  zone,  -         everything delivered into this zone
//     site,  -,     carrier   every zone out of this site on own fleet
//     site,  -,     -         the site default
//
// Carrier type is a dimension because own-fleet diesel and a contractor's
// invoice are not the same expense, and a business that separates them should
// not have to create a rule per zone to say so.
//
// SITE IS THE ZONE'S ORIGIN — the depot that dispatches is the one that pays,
// the same rule the rating page enforces.
//
// NOT SHARED WITH bud_transfer_posting. That table scopes by lane_id into
// bud_transfer_lanes; a secondary lane lives in bud_delivery_lanes. Sharing the
// row would mean a scope column with no foreign key — losing exactly the
// referential integrity the catalogue exists to provide.
//
// ON THE MANTLE GRID, natively, following People › GL Mapping: a catalogue is
// read far more often than it is written, and a chart of accounts that has
// grown to two hundred rules needs header filters and Ctrl+F, not a page of
// <select> elements each holding the whole GL. Every scope column is PICKED
// from a popover rather than typed — an account code typed by hand is an
// account code typed wrong, and the error surfaces at Save.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';

// Specificity, highest first — the same order the SQL LATERAL resolves in.
// If this and that ORDER BY ever disagree, the page lies about which rule wins.
const specificity = (r) => (r.zone_code ? 2 : 0) + (r.carrier_type ? 1 : 0);
const SPEC_LABEL = ['site default', 'carrier', 'zone', 'zone + carrier'];

const CARRIER_OPTS = [
  { code: '', description: 'Both', hint: 'own fleet and contracted alike' },
  { code: 'OWNED', description: 'Own fleet', hint: 'movements on your own vehicles' },
  { code: 'CONTRACTED', description: 'Contracted', hint: 'a transporter’s invoice' },
];

const MUTED = { textDark: '#94a3b8' };
const MISSING = { textDark: '#dc2626' };

let draftSeq = 0;

const SecondaryPosting = () => {
  const { selectedVersion, selectedSite } = useAppContext();

  const [rules, setRules] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [dirty, setDirty] = useState({});      // posting_id -> patch
  const [picks, setPicks] = useState({ sites: [], zones: [], accounts: [], costCenters: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [picker, setPicker] = useState(null);    // { rowKey, field, x, y }
  const [filterUI, setFilterUI] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [postRes, setupRes, zoneRes] = await Promise.all([
        secondaryService.getPosting(),
        secondaryService.getSetup(),
        secondaryService.getZones({ limit: 1000 }),
      ]);
      setRules(postRes.data || []);
      setPicks({
        sites: setupRes.data?.picks?.sites || [],
        zones: zoneRes.data || [],
        accounts: setupRes.data?.picks?.accounts || [],
        costCenters: setupRes.data?.picks?.costCenters || [],
      });
      setDirty({});
      setDrafts([]);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the posting rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion, selectedSite]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const working = useMemo(
    () => [...rules.map((r) => ({ ...r, ...(dirty[r.posting_id] || {}) })), ...drafts]
      .sort((a, b) => String(a.site_code).localeCompare(String(b.site_code))
        || specificity(a) - specificity(b)),
    [rules, dirty, drafts]
  );
  const hasChanges = Object.keys(dirty).length > 0 || drafts.length > 0;

  const keyOf = (r) => r.__draft || r.posting_id;

  // ---- Label resolution ------------------------------------------------------
  const siteName = useCallback((code) => {
    const s = picks.sites.find((x) => x.site_code === code);
    return s ? `${code} · ${s.site_name}` : (code || '');
  }, [picks.sites]);
  const zoneName = useCallback((code) => {
    if (!code) return 'All zones';
    const z = picks.zones.find((x) => x.zone_code === code);
    return z ? `${code} · ${z.zone_name}` : code;
  }, [picks.zones]);
  const accLabel = useCallback((code) => {
    if (!code) return '';
    const a = picks.accounts.find((x) => x.account_code === code);
    return a ? `${code} · ${a.account_description}` : code;
  }, [picks.accounts]);
  const ccLabel = useCallback((code) => {
    if (!code) return '';
    const c = picks.costCenters.find((x) => x.cost_center_code === code);
    return c ? `${code} · ${c.description}` : code;
  }, [picks.costCenters]);
  const carrierLabel = (v) => (v === 'OWNED' ? 'Own fleet' : v === 'CONTRACTED' ? 'Contracted' : 'Both');

  // ---- Filtering -------------------------------------------------------------
  const accessors = useMemo(() => ({
    site_code: (r) => siteName(r.site_code),
    zone_code: (r) => zoneName(r.zone_code),
    carrier_type: (r) => carrierLabel(r.carrier_type),
    account_code: (r) => accLabel(r.account_code),
    cost_center_code: (r) => ccLabel(r.cost_center_code),
  }), [siteName, zoneName, accLabel, ccLabel]);

  const { setFilter, filters, clearAll, filtered, activeIds } = useColumnFilters(working, accessors);

  const filteredRef = useRef([]);
  filteredRef.current = filtered;

  const patch = useCallback((row, p) => {
    if (row.__draft) {
      setDrafts((prev) => prev.map((d) => (d.__draft === row.__draft ? { ...d, ...p } : d)));
      return;
    }
    setDirty((prev) => ({ ...prev, [row.posting_id]: { ...(prev[row.posting_id] || {}), ...p } }));
  }, []);

  const addDraft = () => {
    draftSeq += 1;
    setDrafts((prev) => [...prev, {
      __draft: `d${draftSeq}`,
      site_code: selectedSite || picks.sites[0]?.site_code || '',
      zone_code: null, carrier_type: null,
      account_code: '', cost_center_code: '',
    }]);
  };

  // ---- Grid ------------------------------------------------------------------
  const gcols = useMemo(() => {
    const dot = (id, title) => (activeIds.includes(id) ? `${title} ●` : title);
    return [
      { id: 'site_code', title: dot('site_code', 'Origin Site'), width: 200, hasMenu: true },
      { id: 'zone_code', title: dot('zone_code', 'Zone'), width: 220, hasMenu: true },
      { id: 'carrier_type', title: dot('carrier_type', 'Carrier Type'), width: 130, hasMenu: true },
      { id: 'account_code', title: dot('account_code', 'GL Account'), width: 260, grow: 1, hasMenu: true },
      { id: 'cost_center_code', title: dot('cost_center_code', 'Cost Centre'), width: 220, grow: 1, hasMenu: true },
      { id: 'spec', title: 'Wins Over', width: 120 },
      { id: 'del', title: '', width: 40 },
    ];
  }, [activeIds]);
  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = filtered[row];
    if (!r) return gcell.text('', { readonly: true });
    const edited = !!r.__draft || !!dirty[r.posting_id];
    switch (id) {
      case 'site_code':
        return gcell.text(`${edited ? '• ' : ''}${siteName(r.site_code) || '— pick a site —'}`,
          { readonly: true, theme: r.site_code ? undefined : MISSING });
      // "All zones" is the loosest scope and a legitimate answer, so it reads
      // grey rather than as something missing.
      case 'zone_code':
        return gcell.text(zoneName(r.zone_code), { readonly: true, theme: r.zone_code ? undefined : MUTED });
      case 'carrier_type':
        return gcell.text(carrierLabel(r.carrier_type), { readonly: true, theme: r.carrier_type ? undefined : MUTED });
      // A rule with no account resolves to nothing and the cost posts nowhere —
      // the one genuinely broken state on this page, so it is red.
      case 'account_code':
        return gcell.text(accLabel(r.account_code) || '— posts nowhere —',
          { readonly: true, theme: r.account_code ? undefined : MISSING });
      case 'cost_center_code':
        return gcell.text(ccLabel(r.cost_center_code) || 'none', { readonly: true, theme: r.cost_center_code ? undefined : MUTED });
      case 'spec':
        return gcell.text(SPEC_LABEL[specificity(r)], { readonly: true, theme: MUTED });
      case 'del': return gcell.text('✕', { readonly: true, theme: MUTED });
      default: return gcell.text('', { readonly: true });
    }
  }, [colIds, filtered, dirty, siteName, zoneName, accLabel, ccLabel]);

  const remove = useCallback(async (row) => {
    if (row.__draft) { setDrafts((prev) => prev.filter((d) => d.__draft !== row.__draft)); return; }
    setSaving(true);
    try {
      await secondaryService.deletePosting(row.posting_id);
      setToast('Rule removed.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not remove that rule.');
    } finally { setSaving(false); }
  }, [load]);

  // Every scope column is PICKED, never typed. A GL account typed by hand is a
  // GL account typed wrong, and it only surfaces at Save.
  const onCellClicked = useCallback(([col, row], event) => {
    const r = filteredRef.current[row];
    if (!r) return;
    const id = colIds[col];
    const b = event?.bounds;
    const pos = { x: b ? b.x : 200, y: b ? b.y + b.height : 200 };
    if (id === 'del') { remove(r); return; }
    if (['site_code', 'zone_code', 'carrier_type', 'account_code', 'cost_center_code'].includes(id)) {
      setPicker({ rowKey: keyOf(r), field: id, ...pos });
    }
  }, [colIds, remove]);

  const onHeaderMenu = useCallback((colIndex, bounds) => {
    const colId = colIds[colIndex];
    const acc = accessors[colId];
    if (!acc) return;
    const options = [...new Set(working.map((r) => String(acc(r) ?? '')))].filter(Boolean).sort();
    setFilterUI({ colId, options, x: bounds ? bounds.x : 200, y: bounds ? bounds.y + bounds.height : 200 });
  }, [colIds, accessors, working]);

  const pickerOptions = useMemo(() => {
    if (!picker) return [];
    const row = working.find((r) => keyOf(r) === picker.rowKey);
    switch (picker.field) {
      case 'site_code':
        return picks.sites.map((s) => ({ code: s.site_code, description: s.site_name }));
      // Zones narrow to the rule's own origin site: a rule scoped to a zone
      // dispatched from somewhere else can never resolve, and offering it is
      // offering a mistake.
      case 'zone_code':
        return picks.zones
          .filter((z) => !row?.site_code || z.origin_site_code === row.site_code)
          .map((z) => ({ code: z.zone_code, description: z.zone_name, hint: z.origin_site_code || '' }));
      case 'carrier_type': return CARRIER_OPTS.filter((o) => o.code);
      case 'account_code':
        return picks.accounts.map((a) => ({ code: a.account_code, description: a.account_description }));
      case 'cost_center_code':
        return picks.costCenters.map((c) => ({ code: c.cost_center_code, description: c.description }));
      default: return [];
    }
  }, [picker, picks, working]);

  const applyPick = (code) => {
    const row = working.find((r) => keyOf(r) === picker.rowKey);
    if (row) {
      // Narrowing the site can strand a zone that belongs to a different depot,
      // so the zone clears with it rather than becoming quietly unresolvable.
      const p = { [picker.field]: code || null };
      if (picker.field === 'site_code' && row.zone_code) {
        const z = picks.zones.find((x) => x.zone_code === row.zone_code);
        if (z && z.origin_site_code !== code) p.zone_code = null;
      }
      patch(row, p);
    }
    setPicker(null);
  };

  // ---- Save ------------------------------------------------------------------
  const save = async () => {
    const changed = [
      ...Object.keys(dirty).map((id) => working.find((w) => w.posting_id === id)),
      ...drafts,
    ].filter(Boolean);
    if (!changed.length) return;

    const noSite = changed.find((r) => !r.site_code);
    if (noSite) { setError('Every rule needs an origin site — the depot that dispatches is the one that pays.'); return; }
    const noAccount = changed.find((r) => !r.account_code);
    if (noAccount) { setError('Every rule needs a GL account, or it resolves to nothing and the cost posts nowhere.'); return; }

    // The unique index would reject this, but a 500 quoting an index name is
    // not an answer anybody can act on.
    const key = (r) => `${r.site_code}|${r.zone_code || ''}|${r.carrier_type || ''}`;
    const seen = new Set();
    const dupe = [...working].find((r) => { const k = key(r); if (seen.has(k)) return true; seen.add(k); return false; });
    if (dupe) {
      setError(`Two rules cover the same scope (${dupe.site_code}${dupe.zone_code ? ` · ${dupe.zone_code}` : ''}${dupe.carrier_type ? ` · ${dupe.carrier_type}` : ''}). Narrow one or remove it.`);
      return;
    }

    setSaving(true);
    setError('');
    try {
      for (const r of changed) {
        // eslint-disable-next-line no-await-in-loop
        await secondaryService.savePosting({
          site_code: r.site_code,
          zone_code: r.zone_code || null,
          carrier_type: r.carrier_type || null,
          account_code: r.account_code || null,
          cost_center_code: r.cost_center_code || null,
          notes: r.notes || null,
        });
      }
      setToast(`${changed.length} rule(s) saved.`);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save the posting rules.');
    } finally {
      setSaving(false);
    }
  };

  // A site with lanes but no rule at all posts nowhere. Worth saying loudly:
  // the symptom downstream is a GL Posting Summary that silently omits it.
  const sitesWithZones = useMemo(
    () => [...new Set(picks.zones.map((z) => z.origin_site_code).filter(Boolean))],
    [picks.zones]
  );
  const uncovered = useMemo(
    () => sitesWithZones.filter((s) => !working.some((r) => r.site_code === s)),
    [sitesWithZones, working]
  );
  const filterCount = Object.keys(filters || {}).length;

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Secondary Posting</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Where customer delivery and store replenishment land in the ledger — a GL account and cost centre per site, narrowed by zone or carrier type where it differs. The only place this is decided.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className={btn} onClick={addDraft} disabled={saving}><Plus className="w-3.5 h-3.5" /> Add rule</button>
            <button className={btn} onClick={() => { setDirty({}); setDrafts([]); }} disabled={!hasChanges || saving}><Undo2 className="w-3.5 h-3.5" /> Discard</button>
            <button className={btnPrimary} onClick={save} disabled={!hasChanges || saving}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save{hasChanges ? ` (${Object.keys(dirty).length + drafts.length})` : ''}
            </button>
          </div>
        </div>
        {error && <div className="mt-3 p-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm rounded-md">{error}</div>}
        {toast && <div className="mt-3 p-2.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm rounded-md">{toast}</div>}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {uncovered.length > 0 && (
              <p className="m-0 flex items-start gap-1.5 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
                <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-px" />
                No rule covers <strong>{uncovered.join(', ')}</strong>. Zones dispatch from there, so that cost currently posts to nothing — and it will simply be missing from the GL Posting Summary rather than flagged.
              </p>
            )}

            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 max-h-full flex flex-col overflow-hidden">
              {working.length === 0 ? (
                <p className="m-0 px-2 py-8 text-center text-xs text-slate-400">
                  No rules yet. Start with one per origin site — the loosest rule that still resolves, so nothing posts to nothing.
                </p>
              ) : (
                <MantleDataGrid
                  gridId="dist-secondary-posting"
                  fitHeight
                  fitHeightPadding={6}
                  columns={gcols}
                  rowCount={filtered.length}
                  getCellContent={getCellContent}
                  onCellClicked={onCellClicked}
                  onHeaderMenuClick={onHeaderMenu}
                  freezeColumns={1}
                  hint="Click any scope cell to pick — nothing on this page is typed. ▾ on a heading filters. Ctrl+F searches. ✕ removes a rule. Nothing is written until you press Save."
                  toolbar={<>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      {filtered.length} of {working.length} rule{working.length === 1 ? '' : 's'}
                      {hasChanges && ' · unsaved changes'}
                    </span>
                    {filterCount > 0 && (
                      <button className="text-[11px] font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-200" onClick={clearAll}>
                        Clear filters
                      </button>
                    )}
                  </>}
                />
              )}
            </div>

            <p className="m-0 text-[11px] text-slate-400">
              <strong>Most specific wins.</strong> A <em>zone + carrier</em> rule beats a <em>zone</em> rule, which beats a <em>carrier</em> rule, which beats the <em>site default</em> — so start with one default per site and add narrower rules only where the account genuinely differs. The <strong>Wins Over</strong> column names each rule&rsquo;s level.
              Site is the zone&rsquo;s <strong>origin</strong>: the depot that dispatches is the one that pays, which is the same rule the rating page enforces.
              Backhaul credits post through the same catalogue but usually want their own account — narrow by carrier type, or give that zone its own rule.
            </p>
          </>
        )}
      </div>

      {picker && (
        <PickerPopover
          anchor={{ x: picker.x, y: picker.y }}
          value={working.find((r) => keyOf(r) === picker.rowKey)?.[picker.field] || ''}
          options={pickerOptions}
          placeholder="Search…"
          emptyText="Nothing matches."
          onPick={applyPick}
          onClear={picker.field === 'site_code' || picker.field === 'account_code' ? undefined : () => applyPick(null)}
          clearLabel={picker.field === 'zone_code' ? 'All zones' : picker.field === 'carrier_type' ? 'Both' : 'None'}
          onClose={() => setPicker(null)}
        />
      )}

      {filterUI && (
        <FilterPopover
          key={filterUI.colId}
          anchor={{ x: filterUI.x, y: filterUI.y }}
          options={filterUI.options}
          filter={filters?.[filterUI.colId]}
          onApply={(f) => { setFilter(filterUI.colId, f); setFilterUI(null); }}
          onClose={() => setFilterUI(null)}
        />
      )}
    </div>
  );
};

export default SecondaryPosting;
